import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      setSubmitted(true);
      setTimeout(() => {
        setFormData({ name: '', email: '', phone: '', message: '' });
        setSubmitted(false);
      }, 3000);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - INF LABS</title>
        <meta name="description" content="Get in touch with INF LABS for questions about our peptide products" />
      </Helmet>

      <div className="min-h-screen bg-[var(--color-background)]">
        <Header />
        
        <main className="pt-[100px] pb-12 px-4 md:px-6 lg:px-8">
          <div className="max-w-[1200px] mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Contact Us
            </h1>
            <p className="text-lg text-muted-foreground mb-12">
              Have questions? We're here to help.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Contact Information */}
              <div className="space-y-6">
                <div className="p-6 rounded-xl bg-card border border-border">
                  <h2 className="text-2xl font-bold text-foreground mb-6">
                    Get In Touch
                  </h2>
                  
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-primary/10">
                        <Icon name="Phone" size={24} color="var(--color-primary)" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-foreground mb-1">
                          Phone
                        </h3>
                        <a href="tel:801-810-4671" className="text-primary hover:underline text-lg font-semibold">
                          801-810-4671
                        </a>
                        <p className="text-sm text-muted-foreground mt-1">
                          Text us for order inquiries and support
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-primary/10">
                        <Icon name="Mail" size={24} color="var(--color-primary)" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-foreground mb-1">
                          Email
                        </h3>
                        <a href="mailto:support@inflabs.com" className="text-primary hover:underline">
                          support@inflabs.com
                        </a>
                        <p className="text-sm text-muted-foreground mt-1">
                          We'll respond within 24 hours
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-primary/10">
                        <Icon name="Clock" size={24} color="var(--color-primary)" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-foreground mb-1">
                          Business Hours
                        </h3>
                        <p className="text-foreground">
                          Monday - Friday: 9:00 AM - 6:00 PM MST
                        </p>
                        <p className="text-foreground">
                          Saturday: 10:00 AM - 4:00 PM MST
                        </p>
                        <p className="text-muted-foreground">
                          Sunday: Closed
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-xl bg-primary/10 border border-primary/30">
                  <div className="flex items-start gap-4">
                    <Icon name="MessageSquare" size={32} color="var(--color-primary)" className="flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-bold text-primary mb-2">
                        After Payment Instructions
                      </h3>
                      <p className="text-foreground mb-2">
                        Once you complete your payment, please text the following to:
                      </p>
                      <p className="text-xl font-bold text-primary mb-2">
                        801-810-4671
                      </p>
                      <ul className="list-disc list-inside text-foreground space-y-1">
                        <li>Your full name</li>
                        <li>Your full address</li>
                        <li>Your phone number</li>
                        <li>Products you purchased</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="p-6 rounded-xl bg-card border border-border">
                <h2 className="text-2xl font-bold text-foreground mb-6">
                  Send Us A Message
                </h2>
                
                {submitted ? (
                  <div className="p-6 rounded-lg bg-accent/10 border border-accent/30 text-center">
                    <Icon name="CheckCircle" size={48} color="var(--color-accent)" className="mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-accent mb-2">
                      Message Sent!
                    </h3>
                    <p className="text-foreground">
                      Thank you for contacting us. We'll get back to you soon.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Name *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData?.name}
                        onChange={handleInputChange}
                        placeholder="Your name"
                        className={errors?.name ? 'border-error' : ''}
                      />
                      {errors?.name && (
                        <p className="text-error text-sm mt-1">{errors?.name}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Email *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData?.email}
                        onChange={handleInputChange}
                        placeholder="your.email@example.com"
                        className={errors?.email ? 'border-error' : ''}
                      />
                      {errors?.email && (
                        <p className="text-error text-sm mt-1">{errors?.email}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Phone (Optional)
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData?.phone}
                        onChange={handleInputChange}
                        placeholder="Your phone number"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData?.message}
                        onChange={handleInputChange}
                        placeholder="How can we help you?"
                        rows={5}
                        className={`w-full px-4 py-3 rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all duration-300 ${errors?.message ? 'border-error' : ''}`}
                      />
                      {errors?.message && (
                        <p className="text-error text-sm mt-1">{errors?.message}</p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      variant="default"
                      fullWidth
                      className="h-12 text-base font-semibold"
                    >
                      Send Message
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default Contact;